
import React, { useMemo } from "react";
import classnames from "classnames";
import NotificationAlert from "react-notification-alert";
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  Form,
  Input,
  InputGroupAddon,
  InputGroupText,
  InputGroup,
  Col,
  FormGroup,
  Label,
  Spinner,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import restClient from "../../../api/restClient";
import { getActiveColorStyle } from "../../../utils/colorUtils";
import { establishAuthSession } from "utils/authSession";
import "./Login.css";
import AuthHeroImage from "../components/AuthHeroImage";
import { AUTH_HERO_IMAGE_URLS, AUTH_HERO_SLOGANS } from "../constants";

const getDeviceInfo = () => {
  const userAgent = navigator.userAgent;
  const platform = navigator.platform;
  const language = navigator.language;
  const cookieEnabled = navigator.cookieEnabled;
  const onLine = navigator.onLine;
  const screenWidth = window.screen.width;
  const screenHeight = window.screen.height;
  const colorDepth = window.screen.colorDepth;
  const pixelDepth = window.screen.pixelDepth;
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return {
    userAgent,
    platform,
    language,
    cookieEnabled,
    onLine,
    screenWidth,
    screenHeight,
    colorDepth,
    pixelDepth,
    timezone,
    timestamp: new Date().toISOString(),
  };
};

const getIPAddress = async () => {
  try {
    const response = await fetch("https://api.ipify.org?format=json");
    const data = await response.json();
    return data.ip;
  } catch (error) {
    return "unknown";
  }
};

const IMAGE_URLS = AUTH_HERO_IMAGE_URLS;


const Login = ({ activeColor = "primary" }) => {
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);

  const [state, setState] = React.useState({
    email: "",
    password: "",
    rememberMe: false,
    emailFocus: false,
    passFocus: false,
  });
  const [errors, setErrors] = React.useState({});
  const [showPassword, setShowPassword] = React.useState(false);
  const notificationAlertRef = React.useRef(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [slideIndex, setSlideIndex] = React.useState(0);
  const [overlayActive, setOverlayActive] = React.useState(false); // overlay için state
  const [isLoading, setIsLoading] = React.useState(false);
  React.useEffect(() => {
    document.body.classList.toggle("login-page");
    // Load saved credentials
    const savedEmail = localStorage.getItem("savedEmail");
    const savedPassword = localStorage.getItem("savedPassword");
    if (savedEmail && savedPassword) {
      setState((prev) => ({
        ...prev,
        email: savedEmail,
        password: savedPassword,
        rememberMe: true,
      }));
    }
    return function cleanup() {
      document.body.classList.toggle("login-page");
    };
  }, []);

  React.useEffect(() => {
    let fadeInTimeout, switchTimeout, fadeOutTimeout;
    function startTransitionCycle() {
      // 1. Net halde bekle (ör. 5 sn)
      fadeInTimeout = setTimeout(() => {
        setOverlayActive(true); // 2. Overlay fade-in (siyah + blur)
        // 3. 1.5 sn sonra fotoğraf değişsin
        switchTimeout = setTimeout(() => {
          setSlideIndex((prev) => prev + 1);
          // 4. Yeni fotoğrafla overlay hala açık, 1.5 sn sonra overlay fade-out
          fadeOutTimeout = setTimeout(() => {
            setOverlayActive(false); // 5. Overlay fade-out (netleşir)
          }, 1500); // overlay yeni fotoğrafla 1.5 sn daha açık kalır
        }, 1500); // overlay açıkken eski fotoğraf 1.5 sn kalır
      }, 5000); // net halde 5 sn bekle
    }
    startTransitionCycle();
    const interval = setInterval(() => {
      startTransitionCycle();
    }, 8000); // toplam döngü: 5 sn net + 1.5 sn overlay + 1.5 sn yeni foto overlay = 8 sn
    return () => {
      clearInterval(interval);
      clearTimeout(fadeInTimeout);
      clearTimeout(switchTimeout);
      clearTimeout(fadeOutTimeout);
    };
  }, []);

  const showNotification = (message, type = "warning") => {
    notificationAlertRef.current.notificationAlert({
      place: "tr",
      message: <div>{message}</div>,
      type: type,
      icon: "tim-icons icon-alert-circle-exc",
      autoDismiss: 5,
    });
  };

  const validateForm = () => {
    const newErrors = {};

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!state.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(state.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    // Password validation
    if (!state.password) {
      newErrors.password = "Password is required";
    } else if (state.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    if (!validateForm()) {
      showNotification('Please fix the errors in the form', "warning");
      setIsLoading(false);
      return;
    }

    try {
      const deviceInfo = getDeviceInfo();
      const ipAddress = await getIPAddress();
      const loginData = {
        identifier: state.email,
        password: state.password,
        device: deviceInfo,
        ip_address: ipAddress,
      };
      const response = await restClient.post("login", { data: loginData });
      const token = response.data.jwt;
      if (state.rememberMe) {
        localStorage.setItem("savedEmail", state.email);
        localStorage.setItem("savedPassword", state.password);
      } else {
        localStorage.removeItem("savedEmail");
        localStorage.removeItem("savedPassword");
      }
      establishAuthSession(queryClient, token);
      showNotification("Login successful", "success");
      setTimeout(() => {
        setIsLoading(false);
        navigate("/dashboard");
      }, 1000);
    } catch (error) {
      const errorMessage = error?.response?.data?.error?.message || "Login failed";
      showNotification(errorMessage, "warning");
      setIsLoading(false);
    }
  };

  return (
    <div className="wrapper d-flex" style={{ minHeight: "100vh", width: "100vw" }}>
      <div className="form-section d-flex align-items-center justify-content-center" style={{ flex: 1, background: "#1e1e2f", minWidth: 350, maxWidth: 450, width: "100%", padding: "0px", zIndex: 9999 }}>
        <Col className="ml-auto mr-auto" lg="10" md="12">
          <div className="rna-container">
            <NotificationAlert ref={notificationAlertRef} />
          </div>
          <Form className="form" onSubmit={handleLogin} autoComplete="off" style={{ paddingTop: 0 }}>
            <Card className="card-login" style={{ background: 'transparent', boxShadow: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '20px', color: '#fff', fontWeight: 600, fontSize: 32, marginBottom: 40, letterSpacing: 0.5 }}>
                <img
                  src={process.env.PUBLIC_URL + "/logo.svg"}
                  alt="DealertIQ Logo"
                  style={{ width: '60px', height: '60px', filter: 'brightness(0) invert(1)' }}
                />
                <div style={{ width: '2px', height: '40px', backgroundColor: 'rgba(255,255,255,0.2)' }}></div>
                <span>DEALERTIQ!</span>
              </div>
              <CardBody>
                <FormGroup style={{ marginBottom: 8 }}>
                  <Label for="login-email" style={{ color: '#fff', fontWeight: 500, fontSize: 14, marginBottom: 4 }}>Email address *</Label>
                  <InputGroup
                    style={{ marginBottom: "20px" }}
                    className={classnames({
                      "input-group-focus": state.emailFocus,
                    })}
                  >
                    <InputGroupAddon addonType="prepend">
                      <InputGroupText>
                        <i className="tim-icons icon-email-85" />
                      </InputGroupText>
                    </InputGroupAddon>
                    <Input
                      id="login-email"
                      placeholder="Email"
                      type="email"
                      value={state.email}
                      onChange={(e) => {
                        setState({ ...state, email: e.target.value });
                        if (errors.email) {
                          setErrors(prev => ({ ...prev, email: null }));
                        }
                      }}
                      onFocus={() => setState((prev) => ({ ...prev, emailFocus: true }))}
                      onBlur={() => setState((prev) => ({ ...prev, emailFocus: false }))}
                      autoComplete="username"
                      className={errors.email ? "is-invalid" : ""}
                    />
                  </InputGroup>
                  {errors.email && (
                    <div style={{ color: '#fd5d93', fontSize: '12px', marginTop: '4px' }}>
                      {errors.email}
                    </div>
                  )}
                </FormGroup>
                <FormGroup style={{ marginTop: 0, marginBottom: 8 }}>
                  <Label for="login-password" style={{ color: '#fff', fontWeight: 500, fontSize: 14, marginBottom: 4 }}>Password * (min 6 characters)</Label>
                  <InputGroup
                    style={{ marginBottom: "10px" }}
                    className={classnames({
                      "input-group-focus": state.passFocus,
                    })}
                  >
                    <InputGroupAddon addonType="prepend">
                      <InputGroupText>
                        <i className="tim-icons icon-lock-circle" />
                      </InputGroupText>
                    </InputGroupAddon>
                    <Input
                      id="login-password"
                      placeholder="Password (min 6 characters)"
                      type={showPassword ? "text" : "password"}
                      value={state.password}
                      onChange={(e) => {
                        setState({ ...state, password: e.target.value });
                        if (errors.password) {
                          setErrors(prev => ({ ...prev, password: null }));
                        }
                      }}
                      onFocus={() => setState((prev) => ({ ...prev, passFocus: true }))}
                      onBlur={() => setState((prev) => ({ ...prev, passFocus: false }))}
                      autoComplete="current-password"
                      className={errors.password ? "is-invalid" : ""}
                    />
                    <InputGroupAddon addonType="append">
                      <InputGroupText
                        tag="button"
                        type="button"
                        onClick={() => setShowPassword((prev) => !prev)}
                        style={{
                          cursor: "pointer",
                          background: "transparent",
                          border: "none",
                          color: "#fff",
                        }}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        <i className={showPassword ? "fas fa-eye-slash" : "fas fa-eye"} />
                      </InputGroupText>
                    </InputGroupAddon>
                  </InputGroup>
                  {errors.password && (
                    <div style={{ color: '#fd5d93', fontSize: '12px', marginTop: '4px' }}>
                      {errors.password}
                    </div>
                  )}
                </FormGroup>
                <FormGroup check className="text-left mt-3">
                  <Label check>
                    <Input
                      type="checkbox"
                      style={{
                        accentColor: '#1d8cf8',
                        backgroundColor: state.rememberMe ? '#1d8cf8' : 'transparent',
                        borderColor: state.rememberMe ? '#1d8cf8' : '#2b3553',
                        backgroundImage: state.rememberMe ? 'url("data:image/svg+xml,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 20 20\'%3e%3cpath fill=\'none\' stroke=\'%23fff\' stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'3\' d=\'m6 10 3 3 6-6\'/%3e%3c/svg%3e")' : 'none'
                      }}
                      checked={state.rememberMe}
                      onChange={(e) => setState({ ...state, rememberMe: e.target.checked })}
                    />
                    <span className="form-check-sign" /> Keep me logged in for 15 days
                  </Label>
                </FormGroup>
              </CardBody>
              <CardFooter>
                <Button
                  block
                  className="mb-3"
                  type="submit"
                  disabled={isLoading}
                  size="lg"
                  style={{
                    ...activeColorStyle,
                    border: 'none',
                    color: 'white',
                    boxShadow: 'none',
                    opacity: 1,
                    fontWeight: '600',
                    fontSize: '16px',
                    borderRadius: '0'
                  }}
                >
                  {isLoading ? <Spinner /> : "Login"}
                </Button>
                <Button
                  block
                  className="auth-secondary-btn"
                  type="button"
                  size="md"
                  onClick={() => navigate('/register')}
                  style={{
                    marginTop: 8,
                    backgroundColor: 'transparent',
                    border: 'none',
                    boxShadow: 'none',
                    opacity: 1,
                    fontWeight: '600',
                    marginLeft: "0px",
                    fontSize: '14px',
                    borderRadius: '0',
                    color: '#fff',
                  }}
                >
                  Register
                </Button>
              </CardFooter>
            </Card>
          </Form>
        </Col>
      </div>
      <AuthHeroImage
        imageUrls={IMAGE_URLS}
        slogans={AUTH_HERO_SLOGANS}
        slideIndex={slideIndex}
        overlayActive={overlayActive}
      />
    </div>
  );
};

export default Login;
